import React, { useState, useEffect, useCallback } from 'react';
import useThemeObserver from '../hooks/useThemeObserver';
import './css/navbar.css';

const NAV_LINKS = [
  { href: '#platforms', label: 'Platforms' },
  { href: '#console',   label: 'Console' },
  { href: '#terminal',  label: 'Terminal' },
  { href: '#about',     label: 'About' },
  { href: '#journey',   label: 'Trajectory' },
  { href: '#services',  label: 'Services' },
  { href: '#work',      label: 'Work' },
  { href: '#contact',   label: 'Contact' },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [atTop, setAtTop] = useState(true);
  const [activeSection, setActiveSection] = useState('hero');
  const sectionTheme = useThemeObserver();

  useEffect(() => {
    const handleScroll = () => setAtTop(window.scrollY <= 10);
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1170 && isOpen) {
        setIsOpen(false);
        document.body.style.overflow = '';
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen]);

  useEffect(() => {
    const sections = Array.from(document.querySelectorAll('section[id]'));
    if (!sections.length) return;

    // Activate whichever section's top is currently nearest the viewport's
    // top (just below the navbar). This is far more reliable than
    // IntersectionObserver thresholds when sections vary wildly in height.
    let raf = 0;
    const compute = () => {
      raf = 0;
      const probe = 120; // px below viewport top — under the navbar
      let current = sections[0].id;
      for (const s of sections) {
        const top = s.getBoundingClientRect().top;
        if (top - probe <= 0) current = s.id;
        else break;
      }
      setActiveSection((prev) => (prev === current ? prev : current));
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(compute); };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    compute();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const toggleMenu = useCallback(() => {
    setIsOpen((prev) => {
      const next = !prev;
      document.body.style.overflow = next ? 'hidden' : '';
      return next;
    });
  }, []);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    document.body.style.overflow = '';
  }, []);

  const handleNavClick = useCallback((e, href) => {
    const el = document.querySelector(href);
    if (!el) return;
    e.preventDefault();
    if (window.__lenis) {
      window.__lenis.scrollTo(el, { offset: 0, duration: 1.4 });
    } else {
      el.scrollIntoView({ behavior: 'smooth' });
    }
    setIsOpen(false);
    document.body.style.overflow = '';
    window.history.replaceState(null, '', href);
  }, []);

  const navStyle = {
    '--_nav-bg': sectionTheme.bg,
    '--_nav-text': sectionTheme.text,
    '--_nav-accent': sectionTheme.accent,
    '--_nav-border': sectionTheme.border,
  };

  const headerCls = 'navbar' + (isOpen ? ' menu-open' : '') + (atTop ? ' at-top' : '');

  return (
    <>
      <header className={headerCls} style={navStyle}>
        <div className="navbar-content" onClick={(e) => e.stopPropagation()}>
          <div className="navbar-logo">
            <a href="#hero" onClick={closeMenu}>
              <span className="logo-icon-wrap">
                <img src="/images/icon.png" alt="Logo" className="default-icon" />
                <img src="/images/iconWhite.png" alt="Logo" className="hover-icon" />
              </span>
              <span className="logo-text-group">
                <span className="logo-name">Lake Software Industries</span>
              </span>
            </a>
          </div>

          <nav className="desktop-navbar-links">
            <ul>
              {NAV_LINKS.map(({ href, label }) => (
                <li key={href}>
                  <a
                    href={href}
                    onClick={(e) => handleNavClick(e, href)}
                    className={activeSection === href.slice(1) ? 'active' : ''}
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div
            className={`menu-icon${isOpen ? ' open' : ''}`}
            onClick={toggleMenu}
            role="button"
            aria-label="Toggle menu"
            aria-expanded={isOpen}
          >
            <div className="line" />
            <div className="line" />
            <div className="line" />
          </div>
        </div>
      </header>

      <div className={`mobile-menu-backdrop${isOpen ? ' open' : ''}`} onClick={closeMenu} />
      <div className={`mobile-menu-overlay${isOpen ? ' open' : ''}`} aria-hidden={!isOpen}>
        <nav className="mobile-menu-links">
          <ul>
            {NAV_LINKS.map(({ href, label }, index) => (
              <li key={href} style={{ '--item-index': index }}>
                <a href={href} onClick={(e) => handleNavClick(e, href)} className={activeSection === href.slice(1) ? 'active' : ''}>
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </nav>
        <div className="mobile-menu-social">
          <a href="https://github.com/Joshua-Lake01" className="mobile-social-link" aria-label="GitHub" target="_blank" rel="noopener noreferrer">GH</a>
          <a href="https://www.linkedin.com/in/joshua-lake-jl2412/" className="mobile-social-link" aria-label="LinkedIn" target="_blank" rel="noopener noreferrer">LI</a>
        </div>
      </div>
    </>
  );
};

export default Navbar;
